import classNames from 'classnames';
import { useContext, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Stack from 'react-bootstrap/Stack';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import { v4 as uuidv4 } from 'uuid';
import { FormSubmitContext } from '../../../contexts/FormSubmitContext';
import { DateRangePiker } from '../../DateRangePiker';
import { ProviderScheduleWithKey } from '../Scheduler.types';
import { IShiftPreset, ShiftPreset } from '../ShiftPreset';
import { AVAILABILITY_DAY_FORMAT } from './ProviderShiftSelectorModal.const';
import { FormOutput, ProviderAvailability, ShiftSetType } from './ProviderShiftSelectorModal.types';
import {
  getDatesFromRange,
  getDisableDates,
  getInitialFormValuesForProviderAvailability,
  getProviderPresets,
  getShiftDates,
  getUpdatesDateTime,
  validateDateRange,
  validateIfIsDatesInThePast,
  getInitialShifts,
} from './ProviderShiftSelectorModal.utils';
import './ProviderShiftSelectorModal2.scss';

interface Props {
  isBooking: boolean;
  isLoading: boolean;
  selectedDates: Date[];
  bookedSchedules?: ProviderScheduleWithKey[];
  onConfirm: (data: { shifts: ProviderAvailability[]; comment?: string }) => void;
  onOpenConfirmPopup: () => void;
  isEdit: boolean;
  onRemoveSchedules: () => Promise<void>;
}

export const ProviderShiftSelectorModal2 = ({
  isLoading,
  selectedDates,
  onConfirm,
  onOpenConfirmPopup,
  bookedSchedules,
  isEdit,
  onRemoveSchedules,
}: Props) => {
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const [error, setError] = useState<string>('');

  const initialValues = useMemo(() => {
    return getInitialFormValuesForProviderAvailability({ selectedDates });
  }, [selectedDates]);

  const { control, getValues, setValue, clearErrors, handleSubmit } = useForm<FormOutput>({
    defaultValues: { shiftSets: [initialValues] },
  });
  const { insert: insertToSchedules, remove: removeSchedule } = useFieldArray({
    name: 'shiftSets',
    control: control,
  });

  const formFields = useWatch({ control: control, name: 'shiftSets' });

  const disabledDates = useMemo(() => getDisableDates(bookedSchedules ?? []), [bookedSchedules]);

  const onSubmit = (data: FormOutput) => {
    if (error) return;
    const { shiftSets } = data;
    const res = shiftSets.reduce(
      (acc, item) => {
        acc.dates.push(item.dates);
        acc.shifts.push(item.shifts);
        return acc;
      },
      { dates: [], shifts: [] } as { dates: Date[][]; shifts: ProviderAvailability[][] }
    );
    const shifts: ProviderAvailability[] = res.shifts.flat();
    onConfirm({ shifts });
  };

  const applyPreset = (preset: IShiftPreset): void => {
    const { shiftSets } = getValues();
    const index = preset.parentIndex ?? 0;
    const current = shiftSets[index];
    const presets = current.presets.map((el) => ({
      ...el,
      active: el.type === preset.type,
      partlyActive: el.type === preset.type,
    }));
    const updatedShifts = current.shifts.map((shift) => ({
      ...shift,
      ...getUpdatesDateTime(shift, preset),
    }));
    setValue(`shiftSets.${index}`, {
      ...current,
      shifts: updatedShifts,
      presets,
    });
    clearErrors();
  };

  const addNewDates = () => {
    const index = formFields.length;
    const presets = getProviderPresets({ index });
    const newField: ShiftSetType = {
      dates: [] as Date[],
      shifts: [] as ProviderAvailability[],
      range: ['', ''] as [string, string],
      presets,
      key: uuidv4() as string,
    };
    insertToSchedules(index, newField);
  };

  const onDateRangeChange = (range: { from: string; to: string }, shiftIdx: number) => {
    setError('');
    setValue(`shiftSets.${shiftIdx}.range`, [range.from, range.to], {
      shouldDirty: true,
    });
    const selectedDates = getDatesFromRange(range);
    const { shiftSets } = getValues();
    const { presets } = shiftSets[shiftIdx];
    const activePreset = presets.find((preset) => preset.active!);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const newShifts = getInitialShifts(selectedDates, activePreset);
    if (selectedDates.length && newShifts.length) {
      setValue(`shiftSets.${shiftIdx}.dates`, selectedDates);
      setValue(`shiftSets.${shiftIdx}.shifts`, newShifts);
    }
  };

  const validateRange = (range: {
    from: string;
    to: string;
  }): { from: string | null; to: string | null } => {
    const checkForPastDates = validateIfIsDatesInThePast(range);
    const error = checkForPastDates.from || checkForPastDates.to;
    if (error) {
      setError(error);
      return checkForPastDates;
    }
    const checkForRange = validateDateRange(range, disabledDates);
    checkForRange.from && setError(checkForRange.from);
    return checkForRange;
  };

  const handleRemoveDates = (index: number) => {
    removeSchedule(index);
  };

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)} className="provider-shift-selector-modal">
      <Modal.Header closeButton>
        <Stack direction="vertical" gap={2}>
          <Modal.Title>{isEdit ? 'Modify Your Schedule' : 'Define Your Schedule'}</Modal.Title>
        </Stack>
      </Modal.Header>
      <Modal.Body
        className="available-list"
        style={{ maxHeight: '75vh', height: '100%', minHeight: '452px' }}
      >
        {formFields.map(({ range, key, presets }, shiftIdx) => (
          <section
            className={classNames('available-list-item', {
              'last-item': shiftIdx === formFields.length - 1,
            })}
            key={key}
          >
            <div className="d-flex justify-content-between align-items-center mb-1">
              <h5 className="pb-2 pt-3">Dates</h5>
              {shiftIdx === 0 ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={addNewDates}
                  className="add-new-dates"
                >
                  + Add shift
                </Button>
              ) : (
                <Button
                  variant="link"
                  className="text-danger"
                  size="lg"
                  onClick={() => handleRemoveDates(shiftIdx)}
                  title="delete"
                >
                  <BsTrash />
                </Button>
              )}
            </div>
            <DateRangePiker
              xs={12}
              sm={6}
              mask="__/__/____"
              placeholder="mm/dd/yyyy"
              value={getShiftDates(range)}
              onChange={(range) => onDateRangeChange(range, shiftIdx)}
              dateFormat={AVAILABILITY_DAY_FORMAT}
              disabled={disabledDates}
              compact
              validateRange={validateRange}
            />
            <h5 className="px-2 pb-2 pt-3">Select availability</h5>
            <Row className="preset-list px-2">
              {presets.map((preset, index) => (
                <ShiftPreset
                  id={`${shiftIdx}-${index}`}
                  key={`${shiftIdx}-${index}`}
                  preset={preset}
                  index={index}
                  colSize={12 / presets.length}
                  onApplyPreset={applyPreset}
                />
              ))}
            </Row>
          </section>
        ))}
      </Modal.Body>
      <Modal.Footer
        className={classNames('provider-shift-selector-modal-footer', {
          'justify-content-between': isEdit,
        })}
      >
        {isEdit ? (
          <div className="left-btn">
            <Button variant="danger" onClick={onRemoveSchedules}>
              Remove Availability
            </Button>
          </div>
        ) : null}
        <div className="d-flex gap-2 right-btn">
          <Button variant="outline-primary" disabled={isLoading} onClick={onOpenConfirmPopup}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isLoading || !isSubmitEnabled || !!error}
          >
            {isEdit ? 'Save' : 'Add Availability'}
          </Button>
        </div>
      </Modal.Footer>
    </Form>
  );
};
